// src/pages/VehicleV2/VehicleTransferReceiving.jsx
// Vehicle Transfer Receiving — production listing template + ASPX flow
// Copy over VehicleTransferReceiving.jsx

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaCheckCircle,
  FaSyncAlt,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleTransferReceiving`;

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const clean = (v) => String(v ?? "").trim();

const selectValue = (v) => {
  const s = clean(v);
  return s && s !== "undefined" && s !== "null" ? s : "Select";
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const isFutureDDMMYYYY = (val) => {
  const s = clean(val);
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return false;
  const entered = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  entered.setHours(0, 0, 0, 0);
  return entered.getTime() > today.getTime();
};

const pickField = (row, keys, fallback = "") => {
  if (!row) return fallback;
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== "") {
      return row[k];
    }
  }
  const lower = Object.keys(row).reduce((acc, k) => {
    acc[k.toLowerCase()] = row[k];
    return acc;
  }, {});
  for (const k of keys) {
    const v = lower[String(k).toLowerCase()];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const asOptions = (arr) => {
  const list = toList(arr)
    .map((x) => {
      if (x == null) return null;
      if (typeof x === "string" || typeof x === "number") {
        return { label: String(x), value: String(x) };
      }
      const label = String(
        x.label ??
          x.Label ??
          x.fld_storename ??
          x.fld_vehicle_code ??
          x.fld_vehicle_regno ??
          x.fld_BranchName ??
          x.fld_frombranchname ??
          x.fld_vehicletypename ??
          x.fld_Worklocationname ??
          x.text ??
          ""
      );
      let value =
        x.value ??
        x.Value ??
        x.fld_storeid ??
        x.fld_vehicleid ??
        x.fld_vehicle_frombranchid ??
        x.fld_frombranchid ??
        x.fld_Branchid ??
        x.fld_vehicletypeindex ??
        x.fld_worklocationid ??
        "";
      value = String(value ?? "").trim();
      if (!label && !value) return null;
      if (String(label) === "Select") return { label: "Select", value: "Select" };
      if (!value) return null;
      return { label: label || value, value };
    })
    .filter(Boolean);

  if (!list.some((x) => x.value === "Select")) {
    list.unshift({ label: "Select", value: "Select" });
  }
  return list;
};

const MONTHS = [
  "Select",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultFilters = {
  vehicleMode: "own",
  locationType: "B",
  vehicleCode: "Select",
  vehicleNo: "Select",
  receivedFrom: "Select",
  storeId: "Select",
  locationId: "Select",
  status: "Select",
  month: "Select",
  year: "Select",
  vehicleMainTypeIndex: "Select",
};

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  if (val !== null && val !== undefined && String(val).trim() !== "") return val;
  const sess = sessionStorage.getItem(key);
  if (sess !== null && sess !== undefined && String(sess).trim() !== "") return sess;
  return fallback;
};

const VehicleReceivingEntry = () => {
  const navigate = useNavigate();
  const skipModeEffect = useRef(true);

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId") || getLS("finyearid");
  const finYear = getLS("finYear") || getLS("finyear");
  const branchId =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "0";
  const branchType =
    getLS("branchType") || getLS("branchtype") || getLS("BranchType") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";
  const loginUserId =
    sessionStorage.getItem("loginUserId") ||
    sessionStorage.getItem("loginuserid") ||
    userId ||
    "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);

  const [opts, setOpts] = useState({
    years: [{ label: "Select", value: "Select" }],
    months: MONTHS.map((m) => ({ label: m, value: m })),
    stores: [{ label: "Select", value: "Select" }],
    locations: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    vehicleCodes: [{ label: "Select", value: "Select" }],
    vehicleNos: [{ label: "Select", value: "Select" }],
    receivedFrom: [{ label: "Select", value: "Select" }],
    status: [
      { label: "Select", value: "Select" },
      { label: "Waiting", value: "0" },
      { label: "Received", value: "1" },
    ],
  });

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(rows.length / pageSize)),
    [rows.length]
  );

  const paginatedRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page]
  );

  const isBusy = pageLoading || dropdownLoading || gridLoading || submitting;
  const isSpares = filters.vehicleMode === "spares";

  const fieldClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const buildListParams = useCallback(
    (override = {}) => ({
      branchType,
      branchId,
      loginUserId,
      vehicleMode: override.vehicleMode ?? filters.vehicleMode,
      locationType: override.locationType ?? filters.locationType,
      status: selectValue(override.status ?? filters.status),
      receivedFrom: selectValue(override.receivedFrom ?? filters.receivedFrom),
      vehicleCode: selectValue(override.vehicleCode ?? filters.vehicleCode),
      vehicleNo: selectValue(override.vehicleNo ?? filters.vehicleNo),
      storeId: selectValue(override.storeId ?? filters.storeId),
      locationId: selectValue(override.locationId ?? filters.locationId),
      month: selectValue(override.month ?? filters.month),
      year: selectValue(override.year ?? filters.year),
      vehicleMainType: selectValue(
        override.vehicleMainTypeIndex ?? filters.vehicleMainTypeIndex
      ),
      vehicleMainTypeIndex: selectValue(
        override.vehicleMainTypeIndex ?? filters.vehicleMainTypeIndex
      ),
    }),
    [branchId, branchType, loginUserId, filters]
  );

  const loadDropdowns = useCallback(
    async (locationType = filters.locationType) => {
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { branchId, branchType, loginUserId, locationType },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load initial dropdowns");
      }

      setOpts((prev) => ({
        ...prev,
        years: asOptions(data.years),
        stores: asOptions(data.stores),
        locations: asOptions(data.locations),
        vehicleTypes: asOptions(data.vehicleTypes),
        vehicleCodes: asOptions(data.vehicleCodeList),
        vehicleNos: asOptions(data.vehicleRegNoList),
        receivedFrom: asOptions(data.receivedFromList),
      }));
    },
    [branchId, branchType, loginUserId, commonHeaders, filters.locationType]
  );

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!toStr(loginUserId) || loginUserId === "0") {
        if (!silent) toast.error("loginUserId missing in storage");
        return;
      }
      if (
        String(branchType).toUpperCase() !== "HO" &&
        (!toStr(branchId) || branchId === "0")
      ) {
        if (!silent) toast.error("branchId missing in storage");
        return;
      }

      try {
        setGridLoading(true);
        setHasSearched(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setPage(1);
          if (!silent) toast.warn(data?.message || "No Records found");
          return;
        }

        const raw = toList(data.rows || data.data || data.recordset);
        const uiRows = raw.map((r, idx) => ({
          __rowId: idx + 1,
          __checked: false,
          __receiveDate: todayDDMMYYYY(),
          ...r,
        }));

        setRows(uiRows);
        setPage(1);

        if (!silent) {
          if (!uiRows.length) toast.info("No Records found");
          else toast.success(`Found ${uiRows.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setPage(1);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, branchType, commonHeaders, buildListParams]
  );

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setDropdownLoading(true);
      await loadDropdowns(filters.locationType);
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setDropdownLoading(false);
      setPageLoading(false);
    }
  }, [loadDropdowns, loadList, filters.locationType]);

  const toggleRow = useCallback((rowId, checked) => {
    setRows((prev) =>
      prev.map((r) => (r.__rowId === rowId ? { ...r, __checked: checked } : r))
    );
  }, []);

  const setRowDate = useCallback((rowId, date) => {
    if (isFutureDDMMYYYY(date)) {
      toast.error("Select Receiving Date lesser than Or Equal to current date");
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.__rowId === rowId ? { ...r, __receiveDate: date } : r))
    );
  }, []);

  const handleSearch = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetObj = {
      ...defaultFilters,
      vehicleMode: filters.vehicleMode,
      locationType: filters.locationType,
    };
    setFilters(resetObj);
    await loadList({ silent: false, overrideFilters: resetObj });
  }, [filters.vehicleMode, filters.locationType, loadList]);

  const handleClearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...defaultFilters,
      vehicleMode: prev.vehicleMode,
      locationType: prev.locationType,
    }));
  }, []);

  const openDetails = useCallback(
    (row) => {
      const vehicleId = pickField(row, [
        "fld_vehicleid",
        "fld_sparecatid",
        "vehicleId",
      ]);
      if (!vehicleId) {
        toast.error("Vehicle id missing on this row");
        return;
      }
      sessionStorage.setItem("vehiclereceivingid", String(vehicleId));
      navigate(`/vehicle-transfer-receiving-details/${vehicleId}`);
    },
    [navigate]
  );

  const receiveSelected = useCallback(async () => {
    const selected = rows.filter((r) => r.__checked);

    if (!selected.length) {
      toast.info("Please select Equipment to receive");
      return;
    }

    const missingDate = selected.find((r) => !toStr(r.__receiveDate));
    if (missingDate) {
      toast.error("Please Enter Receiving Date");
      return;
    }

    const future = selected.find((r) => isFutureDDMMYYYY(r.__receiveDate));
    if (future) {
      toast.error("Select Receiving Date lesser than Or Equal to current date");
      return;
    }

    try {
      setSubmitting(true);

      if (filters.vehicleMode === "spares") {
        const payload = {
          branchId,
          items: selected.map((r) => ({
            transferId: pickField(r, [
              "fld_spare_trandferid",
              "fld_spare_transferid",
              "fld_vehicle_trandferid",
              "transferId",
              "id",
              "fld_id",
            ]),
            receiveDate: r.__receiveDate,
          })),
        };

        const { data } = await axios.post(`${API_BASE}/receive/spares`, payload, {
          headers: commonHeaders,
        });

        if (!data?.success) throw new Error(data?.message || "Receive failed");
        toast.success(data.message || "Received successfully");
      } else {
        const payload = {
          branchId,
          loginUserId,
          items: selected.map((r) => ({
            transferId: pickField(r, [
              "fld_vehicle_trandferid",
              "fld_vehtransferid",
              "fld_transferid",
              "transferId",
              "id",
              "fld_id",
            ]),
            receiveDate: r.__receiveDate,
          })),
        };

        const { data } = await axios.post(
          `${API_BASE}/receive/vehicles`,
          payload,
          { headers: commonHeaders }
        );

        if (!data?.success) throw new Error(data?.message || "Receive failed");
        toast.success(data.message || "Equipments Received Successfully");
      }

      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Receive failed"
      );
    } finally {
      setSubmitting(false);
    }
  }, [rows, filters.vehicleMode, branchId, loginUserId, commonHeaders, loadList]);

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipModeEffect.current) {
      skipModeEffect.current = false;
      return;
    }
    const run = async () => {
      try {
        setDropdownLoading(true);
        await loadDropdowns(filters.locationType);
      } catch (err) {
        console.error(err);
      } finally {
        setDropdownLoading(false);
      }
      await loadList({ silent: true });
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.vehicleMode, filters.locationType]);

  const renderRowMeta = (r) => {
    const transferId = pickField(r, [
      "fld_vehicle_trandferid",
      "fld_spare_trandferid",
      "fld_transferid",
      "transferId",
    ]);
    const mainType = pickField(r, [
      "VehicleType",
      "fld_vehicletypename",
      "fld_vehicle_subtypename",
    ]);
    const code = pickField(r, [
      "fld_vehicle_code",
      "fld_sparesvehiclecode",
      "vehicleCode",
      "code",
    ]);
    const regNo = pickField(r, [
      "fld_vehicle_regno",
      "fld_vehicleno",
      "fld_slno",
      "vehicleNo",
    ]);
    const recvFrom = pickField(r, [
      "fld_frombranchname",
      "fld_frombranch",
      "fld_BranchName",
      "receivedFrom",
    ]);
    const fromLoc = pickField(r, [
      "fromlocationname",
      "fld_Worklocationname",
      "fromLocation",
    ]);
    const statusText = pickField(r, ["Status", "status"], "");
    const inward = pickField(r, [
      "fld_vehicle_inward",
      "fld_inward",
      "fld_status",
      "status",
    ]);
    const isReceived =
      inward === 1 ||
      inward === "1" ||
      inward === true ||
      String(inward).toLowerCase() === "true" ||
      String(statusText).toLowerCase() === "received";
    const inwardDate = pickField(r, ["fld_inwarddate", "receivedDate"]);

    return {
      transferId,
      mainType,
      code,
      regNo,
      recvFrom,
      fromLoc,
      statusText,
      isReceived,
      inwardDate,
    };
  };

  const waitingCount = rows.filter((r) => {
    const inward = pickField(r, ["fld_vehicle_inward", "Status"]);
    return !(
      inward === 1 ||
      inward === "1" ||
      String(inward).toLowerCase() === "received"
    );
  }).length;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE TRANSFER RECEIVING
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Receiving Entry
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch {branchId || "-"} • User {loginUserId || "-"} • {branchType} •{" "}
                {isSpares ? "Spares And Catalogue" : "Own Vehicle"}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={receiveSelected}
                disabled={isBusy}
                className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
              >
                <FaCheckCircle />
                {submitting ? "Receiving..." : "Receive Selected"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={() => loadInit()}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaSyncAlt />
                    Refresh
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <div className="flex flex-wrap gap-6 pt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="own"
                        checked={filters.vehicleMode === "own"}
                        onChange={(e) => setField("vehicleMode", e.target.value)}
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-800"}>Own Vehicle</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="spares"
                        checked={filters.vehicleMode === "spares"}
                        onChange={(e) => setField("vehicleMode", e.target.value)}
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-800"}>Spares And Catalogue</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Filter By</label>
                  <div className="flex flex-wrap gap-6 pt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="B"
                        checked={filters.locationType === "B"}
                        onChange={(e) => setField("locationType", e.target.value)}
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-800"}>Branch</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="L"
                        checked={filters.locationType === "L"}
                        onChange={(e) => setField("locationType", e.target.value)}
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-800"}>Location</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  ["Vehicle Code", "vehicleCode", opts.vehicleCodes, isSpares],
                  ["Vehicle Number", "vehicleNo", opts.vehicleNos, isSpares],
                  ["Status", "status", opts.status, false],
                  ["Received From", "receivedFrom", opts.receivedFrom, false],
                  ["Store", "storeId", opts.stores, false],
                  ["From Location", "locationId", opts.locations, false],
                  ["Month", "month", opts.months, false],
                  ["Year", "year", opts.years, false],
                ].map(([lbl, name, options, dis]) => (
                  <div key={name}>
                    <label className={labelClass}>{lbl}</label>
                    <select
                      className={fieldClass}
                      value={filters[name]}
                      onChange={(e) => setField(name, e.target.value)}
                      disabled={isBusy || dis}
                    >
                      {options.map((o, i) => (
                        <option key={`${o.value}-${i}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}

                <div className="md:col-span-2">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    className={fieldClass}
                    value={filters.vehicleMainTypeIndex}
                    onChange={(e) => setField("vehicleMainTypeIndex", e.target.value)}
                    disabled={isBusy}
                  >
                    {opts.vehicleTypes.map((o, i) => (
                      <option key={`${o.value}-${i}`} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {gridLoading || pageLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">Recv</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Main Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Code</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        {isSpares ? "SL.No / Description" : "Vehicle Number"}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Received From</th>
                      {!isSpares && (
                        <th className="px-6 py-4 text-left text-sm font-semibold">From Location</th>
                      )}
                      <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Receiving Date</th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {paginatedRows.map((r, idx) => {
                      const m = renderRowMeta(r);
                      const rowNo = (page - 1) * pageSize + idx;
                      return (
                        <tr
                          key={`${m.transferId || r.__rowId}-${idx}`}
                          className={`${
                            rowNo % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={!!r.__checked || m.isReceived}
                              disabled={isBusy || m.isReceived}
                              onChange={(e) => toggleRow(r.__rowId, e.target.checked)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {rowNo + 1}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {m.mainType || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium">
                            <button
                              type="button"
                              className={isDark ? "text-blue-400 hover:underline" : "text-blue-600 hover:underline"}
                              onClick={() => openDetails(r)}
                            >
                              {m.code || "-"}
                            </button>
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {m.regNo || "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {m.recvFrom || "-"}
                          </td>
                          {!isSpares && (
                            <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {m.fromLoc || "-"}
                            </td>
                          )}
                          <td
                            className={`px-6 py-4 text-sm font-semibold ${
                              m.isReceived
                                ? isDark ? "text-green-400" : "text-green-600"
                                : isDark ? "text-yellow-400" : "text-yellow-600"
                            }`}
                          >
                            {m.isReceived ? "Received" : m.statusText || "Waiting"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {m.isReceived ? (
                              <span className={isDark ? "text-gray-300" : "text-gray-900"}>
                                {m.inwardDate || "-"}
                              </span>
                            ) : (
                              <input
                                className={`px-2 py-1 border rounded text-sm w-32 ${
                                  isDark
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                                value={r.__receiveDate}
                                onChange={(e) => setRowDate(r.__rowId, e.target.value)}
                                placeholder="dd/MM/yyyy"
                                disabled={isBusy}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Records: <span className="font-semibold">{rows.length}</span>
                    {" • "}
                    Waiting: <span className="font-semibold">{waitingCount}</span>
                  </div>
                  {totalPages > 1 && (
                    <nav className="flex items-center gap-2 flex-wrap">
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setPage(i + 1)}
                          disabled={isBusy}
                          className={`px-3 py-1 rounded text-sm font-semibold ${
                            page === i + 1
                              ? `${buttonClass} text-white`
                              : isDark
                              ? "bg-gray-700 text-white"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </nav>
                  )}
                </div>
              </>
            )}
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleReceivingEntry;